import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReconciliationModel } from '../../models/client-reconciliation';
import { AdxPaymentService } from '../../service/adx-payment.service';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MaskedCardPipe } from 'src/infrastructure/pipes/masked-card.pipe';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { AutoUnsubscribe } from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import { MatSnackBar } from '@angular/material/snack-bar';

@AutoUnsubscribe()
@Component({
    selector: 'client-payments-edit-modal',
    templateUrl: './client-payments-edit-modal.component.html',
    styleUrls: ['./client-payments-edit-modal.component.scss'],
    standalone: false
})
export class ClientPaymentsEditModalComponent implements OnInit {
  displayedColumns: string[] = [
    'adxInvoiceId',
    'adxBookingId',
    'adxBookingTravelTypeName',
    'adxBookingVendorName',
    'totalFare',
    'vendorPaymentMadeAmt',
    'clientPaymentReceivedAmt'
  ];

  loadedData = false;
  submitedData = true;
  dataSource: any[] = [];
  formControlNames: {
    [key: string]: {
      totalFare: string, vendorPaymentMadeAmt: string, clientPaymentReceivedAmt: string
    } 
  } = {};

  formChanges$?: Subscription;
  dynamicFormGroup?: FormGroup;
  initialValues: Record<string, string> = {};
  hasChanges = false;

  amountPattern = /^-?\d{1,10}(\.\d{1,4})?$/;

  pagination: Partial<PageEvent & { totalItemCount?: number }> = {};
  sorting: Partial<Sort> = {};

  constructor(
    @Optional() public dialogRef: MatDialogRef<ClientPaymentsEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReconciliationModel,
    private adxPaymentService: AdxPaymentService,
    private maskedCardPipe: MaskedCardPipe,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    if (!this.data) {
      this.dialogRef.close();
    }

    this.pagination = {
      pageSize: this.pagination.pageSize || 25,
      pageIndex: 0,
      totalItemCount: 0,
    };

    this.getItems();
  }

  buildForm(): void {
    const formControllers = this.getFormControlers();
    this.dynamicFormGroup = new FormGroup(formControllers);

    this.dataSource.forEach((payment) => {
      const adxBookingId = payment.adxBookingId;

      if (!this.formControlNames[adxBookingId]) {
        this.formControlNames[adxBookingId] = {
          totalFare: `totalFare_${adxBookingId}`,
          vendorPaymentMadeAmt: `vendorPaymentMadeAmt_${adxBookingId}`,
          clientPaymentReceivedAmt: `clientPaymentReceivedAmt_${adxBookingId}`
        };
      }

      const formControlNames = this.formControlNames[adxBookingId];

      if (this.dynamicFormGroup) {
        this.initialValues[`totalFare_${adxBookingId}`] = payment.totalFare === null || payment.totalFare === undefined ? 0 : payment.totalFare;
        this.initialValues[`vendorPaymentMadeAmt_${adxBookingId}`] = payment.vendorPaymentMadeAmt === null || payment.vendorPaymentMadeAmt === undefined ? 0 : payment.vendorPaymentMadeAmt;
        this.initialValues[`clientPaymentReceivedAmt_${adxBookingId}`] = payment.clientPaymentReceivedAmt === null || payment.clientPaymentReceivedAmt === undefined ? 0 : payment.clientPaymentReceivedAmt;

        this.dynamicFormGroup.addControl(formControlNames.totalFare, new FormControl(this.initialValues[`totalFare_${adxBookingId}`]));
        this.dynamicFormGroup.addControl(formControlNames.vendorPaymentMadeAmt, new FormControl(this.initialValues[`vendorPaymentMadeAmt_${adxBookingId}`]));
        this.dynamicFormGroup.addControl(formControlNames.clientPaymentReceivedAmt, new FormControl(this.initialValues[`clientPaymentReceivedAmt_${adxBookingId}`]));
      }
    });
  }

  getFormControlers(): Record<string, FormControl> {
    const formList: Record<string, FormControl> = {};
    this.dataSource.forEach((payment) => {
      const adxBookingId = payment.adxBookingId;

      const totalFare = payment.totalFare !== null && payment.totalFare !== undefined ? payment.totalFare : 0;
      const vendorPaymentMadeAmt = payment.vendorPaymentMadeAmt !== null && payment.vendorPaymentMadeAmt !== undefined ? payment.vendorPaymentMadeAmt : 0;
      const clientPaymentReceivedAmt = payment.clientPaymentReceivedAmt !== null && payment.clientPaymentReceivedAmt !== undefined ? payment.clientPaymentReceivedAmt : 0;
      
      formList[`totalFare_${adxBookingId}`] = new FormControl(totalFare, Validators.pattern(this.amountPattern));
      formList[`vendorPaymentMadeAmt_${adxBookingId}`] = new FormControl(vendorPaymentMadeAmt, Validators.pattern(this.amountPattern));
      formList[`clientPaymentReceivedAmt_${adxBookingId}`] = new FormControl(clientPaymentReceivedAmt, Validators.pattern(this.amountPattern));
    });

    return formList;
  }

  handleSortChange(sort: Sort): void {
    this.sorting = sort;

    if (!this.sorting?.active || !this.sorting?.direction) {
      this.sorting = {};
    }

    this.getItems();
  }

  handlePageChanged(page: PageEvent): void {
    this.pagination = {
      ...this.pagination,
      ...page,
    };

    this.getItems();
  }

  getItems(): void {
    this.loadedData = false;
    
    const filters: any = {};
    
    if (this.data.adxReference) {
      filters.tripExternalReference = this.data.adxReference;
    }
    
    if (this.data.vendorName && this.data.vendorName.trim() !== '') {
      filters.vendorName = this.data.vendorName;
    }
    
    if (this.data.lastFour) {
      filters.ccNoLastFourDigits = this.maskedCardPipe.transform(this.data.lastFour, '');
    }
    
    if (this.data.payeeName) {
      filters.payeeName = this.data.payeeName;
    }
    
    if (this.data.clientNameAlias) {
      filters.clientName = this.data.clientNameAlias;
    }
    
    if (this.data.ccProcessorId) {
      filters.ccProcessorId = this.data.ccProcessorId;
    }
    
    if (this.data.reconType?.toUpperCase() === 'I') {
      if (this.data.fmsAgentName) {
        filters.agentName = this.data.fmsAgentName;
      }
      
      if (this.data.dataSourceId) {
        filters.dataSourceId = this.data.dataSourceId;
      }
      
      if (this.data.fmsPaymentAmountTotal) {
        filters.paymentAmount = this.data.fmsPaymentAmountTotal;
      }
      
      if (this.data.fmsBankId) {
        filters.bankId = this.data.fmsBankId;
      }
      
      if (this.data.fmsBranchId) {
        filters.branchId = this.data.fmsBranchId;
      }
    }
    this.tryAlternativeEndpoint(filters);
  }

  private tryAlternativeEndpoint(filters: any): void {
    const essentialFilters: Record<string, any> = {
      tripExternalReference: filters.tripExternalReference
    };
    
    if (filters.ccNoLastFourDigits) {
      essentialFilters['ccNoLastFourDigits'] = filters.ccNoLastFourDigits;
    }
    
    if (filters.clientName) {
      essentialFilters['clientName'] = filters.clientName;
    }
    
    this.adxPaymentService.searchAdxPaymentReconciliation({
      filters: essentialFilters,
      page: this.pagination, 
      sort: this.sorting,
    }).subscribe({
      next: (data: { items: any[]; pagination: { pageNumber: number; pageSize: any; totalItemCount: any; }; }) => {
        this.processApiResponse(data);
      },
      error: (error) => {
        console.error('Error from alternative endpoint:', error);
        
        if (filters.tripExternalReference) {
          const directFilter = { tripExternalReference: filters.tripExternalReference };
          
          this.adxPaymentService.getReconciliation({
            filters: directFilter,
            page: this.pagination,
            sort: this.sorting,
          }).subscribe({
            next: (data: { items: any[]; pagination: { pageNumber: number; pageSize: any; totalItemCount: any; }; }) => {
              this.processApiResponse(data);
            },
            error: (finalError) => {
              console.error('All attempts failed:', finalError);
              this.loadedData = true;
              this.showError('Failed to load payment details. Please try again.');
            }
          });
        } else {
          this.loadedData = true;
          this.showError('Could not load services. Missing reference information.');
        }
      }
    });
  }
  
  private showError(message: string): void {
    this.snackBar.open(message, 'Dismiss', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }
  
  private processApiResponse(data: { items: any[]; pagination: { pageNumber: number; pageSize: any; totalItemCount: any; }; }): void {
    this.dataSource = [];
    
    const aggregatedPayments = new Map<string, any>();
    
    if (data?.items && Array.isArray(data.items)) {
      data.items.forEach(item => {
        const bookingId = item.adxBookingId;
        const uniqueKey = `${item.adxInvoiceId}_${bookingId}`;
        
        if (!aggregatedPayments.has(uniqueKey)) {
          aggregatedPayments.set(uniqueKey, {
            adxInvoiceId: item.adxInvoiceId,
            adxBookingId: bookingId,
            adxBookingTravelTypeName: item.adxBookingTravelTypeName,
            adxBookingVendorName: item.adxBookingVendorName,
            totalFare: 0,
            vendorPaymentMadeAmt: 0,
            clientPaymentReceivedAmt: 0,
            tripId: item.tripId
          });
        }
        
        const aggregated = aggregatedPayments.get(uniqueKey);
        
        const amount = Number(item.amount) || 0;
        
        if (item.payTypeId === 1) {
          aggregated.clientPaymentReceivedAmt += amount;
          if (aggregated.totalFare === 0) {
            aggregated.totalFare = amount;
          }
        } else if (item.payTypeId === 2) {
          aggregated.vendorPaymentMadeAmt += amount;
        }
      });
      
      this.dataSource = Array.from(aggregatedPayments.values());
    }
    
    this.pagination = {
      ...this.pagination,
      pageIndex: data.pagination?.pageNumber ? data.pagination.pageNumber - 1 : 0,
      pageSize: data.pagination?.pageSize || this.pagination.pageSize || 25,
      totalItemCount: data.pagination?.totalItemCount || this.dataSource.length,
    };
    this.loadedData = true;
    this.buildForm();
  }

  isDisabled(): boolean {
    return !this.dataSource.some((payment) => {
      const adxBookingId = payment.adxBookingId;
      const formControlNames = this.formControlNames[adxBookingId];

      const totalFareValue = this.dynamicFormGroup?.get(formControlNames.totalFare)?.value;
      const vendorPaymentMadeAmtValue = this.dynamicFormGroup?.get(formControlNames.vendorPaymentMadeAmt)?.value;
      const clientPaymentReceivedAmtValue = this.dynamicFormGroup?.get(formControlNames.clientPaymentReceivedAmt)?.value;

      const initialTotalFare = Number(this.initialValues[`totalFare_${adxBookingId}`]);
      const initialVendorPaymentMadeAmt = Number(this.initialValues[`vendorPaymentMadeAmt_${adxBookingId}`]);
      const initialClientPaymentReceivedAmt = Number(this.initialValues[`clientPaymentReceivedAmt_${adxBookingId}`]);

      const totalFare = totalFareValue !== '' && totalFareValue !== null ? Number(totalFareValue) : initialTotalFare;
      const vendorPaymentMadeAmt = vendorPaymentMadeAmtValue !== '' && vendorPaymentMadeAmtValue !== null ? Number(vendorPaymentMadeAmtValue) : initialVendorPaymentMadeAmt;
      const clientPaymentReceivedAmt = clientPaymentReceivedAmtValue !== '' && clientPaymentReceivedAmtValue !== null ? Number(clientPaymentReceivedAmtValue) : initialClientPaymentReceivedAmt;

      return totalFare !== initialTotalFare ||
        vendorPaymentMadeAmt !== initialVendorPaymentMadeAmt ||
        clientPaymentReceivedAmt !== initialClientPaymentReceivedAmt;
    });
  }

  updateAmounts(): void {
    this.submitedData = false;
    const updates$: Observable<any>[] = [];

    const changedPayments = this.dataSource.filter((payment) => {
      const adxBookingId = payment.adxBookingId;
      const formControlNames = this.formControlNames[adxBookingId];

      const totalFareValue = this.dynamicFormGroup?.get(formControlNames.totalFare)?.value;
      const vendorPaymentMadeAmtValue = this.dynamicFormGroup?.get(formControlNames.vendorPaymentMadeAmt)?.value;
      const clientPaymentReceivedAmtValue = this.dynamicFormGroup?.get(formControlNames.clientPaymentReceivedAmt)?.value;

      const initialTotalFare = Number(this.initialValues[`totalFare_${adxBookingId}`]);
      const initialVendorPaymentMadeAmt = Number(this.initialValues[`vendorPaymentMadeAmt_${adxBookingId}`]);
      const initialClientPaymentReceivedAmt = Number(this.initialValues[`clientPaymentReceivedAmt_${adxBookingId}`]);

      const totalFare = totalFareValue !== '' && totalFareValue !== null ? Number(totalFareValue) : initialTotalFare;
      const vendorPaymentMadeAmt = vendorPaymentMadeAmtValue !== '' && vendorPaymentMadeAmtValue !== null ? Number(vendorPaymentMadeAmtValue) : initialVendorPaymentMadeAmt;
      const clientPaymentReceivedAmt = clientPaymentReceivedAmtValue !== '' && clientPaymentReceivedAmtValue !== null ? Number(clientPaymentReceivedAmtValue) : initialClientPaymentReceivedAmt;

      var compare =  totalFare !== initialTotalFare ||
        vendorPaymentMadeAmt !== initialVendorPaymentMadeAmt ||
        clientPaymentReceivedAmt !== initialClientPaymentReceivedAmt;

      return compare;
    });

    changedPayments.forEach((payment) => {
      const adxBookingId = payment.adxBookingId;
      const formControlNames = this.formControlNames[adxBookingId];

      const totalFareValue = this.dynamicFormGroup?.get(formControlNames.totalFare)?.value;
      const vendorPaymentMadeAmtValue = this.dynamicFormGroup?.get(formControlNames.vendorPaymentMadeAmt)?.value;
      const clientPaymentReceivedAmtValue = this.dynamicFormGroup?.get(formControlNames.clientPaymentReceivedAmt)?.value;

      const initialTotalFare = Number(this.initialValues[`totalFare_${adxBookingId}`]);
      const initialVendorPaymentMadeAmt = Number(this.initialValues[`vendorPaymentMadeAmt_${adxBookingId}`]);
      const initialClientPaymentReceivedAmt = Number(this.initialValues[`clientPaymentReceivedAmt_${adxBookingId}`]);

      const totalFare = totalFareValue !== '' && totalFareValue !== null ? Number(totalFareValue) : initialTotalFare;
      const vendorPaymentMadeAmt = vendorPaymentMadeAmtValue !== '' && vendorPaymentMadeAmtValue !== null ? Number(vendorPaymentMadeAmtValue) : initialVendorPaymentMadeAmt;
      const clientPaymentReceivedAmt = clientPaymentReceivedAmtValue !== '' && clientPaymentReceivedAmtValue !== null ? Number(clientPaymentReceivedAmtValue) : initialClientPaymentReceivedAmt;
 
      updates$.push(
          this.adxPaymentService.adjustmentAdxPaymentReconciliation({
            adxBookingId: payment.adxBookingId,
            amount: totalFare !== initialTotalFare? totalFare - initialTotalFare : null,
            adxTripId: this.dataSource[0].tripId,
            bankId: this.data.fmsBankId,
            branchId: this.data.fmsBranchId,
            ckCcNo: this.data.originalCkCcNo,
            payeeName: this.data.fmsPayeeName,
            vendorPaymentMadeAmt: vendorPaymentMadeAmt !== initialVendorPaymentMadeAmt ? vendorPaymentMadeAmt - initialVendorPaymentMadeAmt : null,
            clientPaymentReceivedAmt: clientPaymentReceivedAmt !== initialClientPaymentReceivedAmt ? clientPaymentReceivedAmt - initialClientPaymentReceivedAmt : null
          })
        );
      }
    );

    if (updates$.length > 0) {
      forkJoin(updates$).subscribe({
        next: () => {
          this.snackBar.open('Service adjusted successfully', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            duration: 3000,
          });
          this.getItems();
          this.submitedData = true;
        },
        error: () => {
          this.submitedData = true;
        }
      });
    } else {
      this.snackBar.open('No changes detected', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 3000,
      });
      this.submitedData = true;
    }
  }


  closeModal(): void {
    this.dialogRef.close();
  }
}
